'use client'

import Script from 'next/script'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { trackPageView } from '../../lib/tracking'

function useConsentGiven(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem('hmn-cookie-consent') === 'accepted'
}

export function TrackingPixels() {
  const pathname = usePathname()
  const prevPath = useRef(pathname)
  const enabled = process.env.NEXT_PUBLIC_ENABLE_ANALYTICS === 'true'

  const gaId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID
  const metaId = process.env.NEXT_PUBLIC_META_PIXEL_ID
  const linkedinId = process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID
  // Fire page view on SPA route changes
  useEffect(() => {
    if (prevPath.current !== pathname) {
      prevPath.current = pathname
      trackPageView(pathname, document.title)

      // Meta Pixel SPA page view
      if (window.fbq) window.fbq('track', 'PageView')
    }
  }, [pathname])

  if (!enabled) return null

  const consentGiven = useConsentGiven()

  return (
    <>
      {/* ---------------------------------------------------------------- */}
      {/* GA4 — always loads (essential analytics, anonymized if no consent) */}
      {/* ---------------------------------------------------------------- */}
      {gaId && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
            strategy="afterInteractive"
          />
          <Script id="ga4-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${gaId}', {
                send_page_view: true,
                ${!consentGiven ? "anonymize_ip: true," : ''}
              });
            `}
          </Script>
        </>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Below pixels only load with consent */}
      {/* ---------------------------------------------------------------- */}
      {consentGiven && (
        <>
          {/* Meta Pixel */}
          {metaId && (
            <>
              <Script id="meta-pixel" strategy="afterInteractive">
                {`
                  !function(f,b,e,v,n,t,s)
                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                  'https://connect.facebook.net/en_US/fbevents.js');
                  fbq('init', '${metaId}');
                  fbq('track', 'PageView');
                `}
              </Script>
              <noscript>
                <img
                  height="1"
                  width="1"
                  style={{ display: 'none' }}
                  src={`https://www.facebook.com/tr?id=${metaId}&ev=PageView&noscript=1`}
                  alt=""
                />
              </noscript>
            </>
          )}

          {/* LinkedIn Insight Tag */}
          {linkedinId && (
            <>
              <Script id="linkedin-insight" strategy="afterInteractive">
                {`
                  _linkedin_partner_id = "${linkedinId}";
                  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
                  window._linkedin_data_partner_ids.push(_linkedin_partner_id);
                  (function(l) {
                    if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
                    window.lintrk.q=[]}
                    var s = document.getElementsByTagName("script")[0];
                    var b = document.createElement("script");
                    b.type = "text/javascript";b.async = true;
                    b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
                    s.parentNode.insertBefore(b, s);})(window.lintrk);
                `}
              </Script>
              <noscript>
                <img
                  height="1"
                  width="1"
                  style={{ display: 'none' }}
                  alt=""
                  src={`https://px.ads.linkedin.com/collect/?pid=${linkedinId}&fmt=gif`}
                />
              </noscript>
            </>
          )}
        </>
      )}
    </>
  )
}
